/* Cookie banner — brand shell + granular consent.
   Design source: claude.ai/design project b3cdbd17 → "Cookie Banner.html".

   Timeline for a first-time visitor:
     +0.8s  the bar slides in bottom left (18px, 500ms)
     click  "Accept all" / "Necessary only" decide at once; "Manage" opens the
            analytics + marketing switches in place and relabels the two
            buttons to "Save choices" / "Accept all"
     +0.0s  the bar leaves, a confirmation toast takes its place
     +2.0s  the toast fades
     +4.0s  the newsletter bubble is released and runs its own choreography

   After a decision the corner stays empty: the way back in is the footer's
   "Cookie preferences" button ([data-ck-open]) on every breakpoint. The corner
   is shared with the newsletter bubble, so the two are sequenced rather than
   stacked: nothing from the newsletter renders until a cookie decision exists
   (newsletter-popup-v2.js asks newsletterAllowed() before it shows anything).

   Memory (localStorage `tl_cookie`): {necessary, analytics, marketing, ts},
   re-asked after `data-remember-days`. Shopify's Customer Privacy API is what
   actually gates GA4 / Meta / TikTok — localStorage only drives this UI and
   the sequencing above. */
(function () {
  var KEY = 'tl_cookie';
  var DAY = 86400000;

  var root = document.querySelector('[data-tl-cookie]');
  if (!root) return;

  var bar = root.querySelector('[data-ck-bar]');
  var prefs = root.querySelector('[data-ck-prefs]');
  var swAnalytics = root.querySelector('[data-ck-analytics]');
  var swMarketing = root.querySelector('[data-ck-marketing]');
  var btnPrimary = root.querySelector('[data-ck-primary]');
  var btnSecondary = root.querySelector('[data-ck-secondary]');
  var btnManage = root.querySelector('[data-ck-manage]');
  var toast = document.querySelector('[data-ck-toast]');
  var toastText = toast && toast.querySelector('[data-ck-toast-text]');

  var designMode = root.dataset.designMode === 'true';
  var delay = parseInt(root.dataset.delay, 10);
  if (isNaN(delay)) delay = 800;
  var rememberDays = parseInt(root.dataset.rememberDays, 10) || 180;

  var visible = false; // the bar is on screen
  var reopened = false; // opened from the footer link rather than on arrival
  var released = false; // the newsletter bubble may run
  var timers = [];

  function later(fn, ms) {
    timers.push(setTimeout(fn, ms));
  }
  function clearTimers() {
    timers.forEach(clearTimeout);
    timers = [];
  }

  /* ------------------------------------------------------------- memory */

  function readStored() {
    var raw;
    try {
      raw = JSON.parse(localStorage.getItem(KEY));
    } catch (x) {
      return null;
    }
    if (!raw || typeof raw !== 'object' || !raw.ts) return null;
    if (Date.now() - raw.ts > rememberDays * DAY) return null; // time to ask again
    return raw;
  }

  function writeStored(prefsObj) {
    try {
      localStorage.setItem(KEY, JSON.stringify(prefsObj));
    } catch (x) {}
  }

  /* -------------------------------------------------- Shopify consent API */

  function privacy() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  /* Loads the consent feature, then hands the API to `done` (or null when it
     is unavailable — the bar still works, it just cannot gate Shopify's own
     tracking until the API turns up). */
  function loadPrivacy(done) {
    if (privacy()) {
      done(privacy());
      return;
    }
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      done(null);
      return;
    }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      document.dispatchEvent(new CustomEvent('eurus:customer-privacy:loaded'));
      done(error ? null : privacy());
    });
  }

  /* Our three categories map onto Shopify's four: preferences travel with
     analytics (both are "how the shop behaves for you"), sale of data travels
     with marketing. */
  function applyConsent(prefsObj) {
    var api = privacy();
    if (!api || typeof api.setTrackingConsent !== 'function') return;
    try {
      api.setTrackingConsent(
        {
          analytics: !!prefsObj.analytics,
          preferences: !!prefsObj.analytics,
          marketing: !!prefsObj.marketing,
          sale_of_data: !!prefsObj.marketing
        },
        function () {}
      );
    } catch (x) {}
  }

  /* ------------------------------------------------------- newsletter gate */

  /* Deliberately not in `timers`: re-opening the panel within the handover
     window must not strand the bubble behind a gate that never lifts. */
  var releaseTimer = null;
  function release(ms) {
    if (released || releaseTimer) return;
    releaseTimer = setTimeout(function () {
      releaseTimer = null;
      released = true;
      document.dispatchEvent(new CustomEvent('tl:cookie-newsletter-release'));
    }, ms);
  }

  window.TLCookieConsent = {
    decided: function () {
      return !!readStored();
    },
    get: function () {
      return readStored();
    },
    /* false until a choice exists AND the corner has been handed over */
    newsletterAllowed: function () {
      return released;
    },
    open: function () {
      openBar(true);
    }
  };

  /* ------------------------------------------------------------ placement */

  /* offsetParent is null for position:fixed elements, so it cannot be used to
     ask whether one of these overlays is on screen — go by the box instead. */
  function boxOf(selector) {
    var el = document.querySelector(selector);
    if (!el || !el.getClientRects().length) return null;
    var r = el.getBoundingClientRect();
    return r.height > 0 ? r : null;
  }

  /* Clear the sticky Add to cart bar rather than covering it. Measured from its
     top edge so this holds for the full-width mobile bar and the floating
     desktop one alike. */
  var atcLift = 0;
  var lastATC = null;
  function trackStickyATC() {
    var r = boxOf('.sticky_add_to_cart');
    atcLift = r ? Math.max(0, Math.round(window.innerHeight - r.top + 12)) : 0;
    var value = atcLift + 'px';
    if (value === lastATC) return;
    lastATC = value;
    document.documentElement.style.setProperty('--ck-atc', value);
  }

  function measure() {
    trackStickyATC();
  }

  /* ------------------------------------------------------------- the bar */

  /* Document order, not the order these were declared in: the switches sit
     above the buttons in the markup, so a hand-written list would put the wrap
     points on the wrong elements and Tab would walk straight out of the bar. */
  function focusables() {
    return Array.prototype.filter.call(bar.querySelectorAll('button, input'), function (el) {
      return !el.disabled && !el.closest('[inert]');
    });
  }

  function setManage(open) {
    bar.classList.toggle('is-open', open);
    btnManage.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) prefs.removeAttribute('inert');
    else prefs.setAttribute('inert', '');
    [btnPrimary, btnSecondary, btnManage].forEach(function (btn) {
      btn.textContent = open ? btn.dataset.labelOpen : btn.dataset.labelClosed;
    });
  }

  function showBar() {
    if (visible) return;
    visible = true;
    root.hidden = false;
    root.classList.remove('is-out');
    document.documentElement.classList.add('tl-ck-busy');
    // Flush the display change so the transition has a start value to run from.
    // requestAnimationFrame is the usual idiom for this, but it never fires
    // while the tab is in the background — the banner would sit at opacity 0
    // with the class that reveals it queued behind a frame that never comes.
    void bar.offsetWidth;
    root.classList.add('is-in');
  }

  function hideBar() {
    if (!visible) return;
    visible = false;
    root.classList.remove('is-in');
    root.classList.add('is-out');
    document.documentElement.classList.remove('tl-ck-busy');
    later(function () {
      if (!visible) root.hidden = true;
    }, 420);
  }

  function openBar(fromGear) {
    clearTimers();
    reopened = !!fromGear;
    var stored = readStored();
    swAnalytics.checked = stored ? !!stored.analytics : root.dataset.analyticsDefault === 'true';
    swMarketing.checked = stored ? !!stored.marketing : false;
    hideToast();
    setManage(!!fromGear);
    showBar();
    if (fromGear) {
      later(function () {
        btnPrimary.focus();
      }, 120);
    }
  }

  function showToast(label) {
    if (!toast) return;
    toastText.textContent = label;
    toast.hidden = false;
    void toast.offsetWidth;
    toast.classList.add('is-in');
  }

  function hideToast() {
    if (!toast) return;
    toast.classList.remove('is-in');
    later(function () {
      if (!toast.classList.contains('is-in')) toast.hidden = true;
    }, 360);
  }

  /* ------------------------------------------------------------- decide */

  function save(analytics, marketing, label) {
    var prefsObj = { necessary: true, analytics: !!analytics, marketing: !!marketing, ts: Date.now() };
    writeStored(prefsObj);
    applyConsent(prefsObj);
    document.dispatchEvent(new CustomEvent('tl:cookie-consent', { detail: prefsObj }));

    clearTimers();
    setManage(false);
    hideBar();
    showToast(label);
    later(hideToast, 2000);
    release(4000); // the corner is the newsletter's again
    reopened = false;
  }

  /* Closing a re-opened panel is not a decision — the stored choice stands. */
  function dismissReopened() {
    clearTimers();
    setManage(false);
    hideBar();
    reopened = false;
  }

  /* --------------------------------------------------------------- wiring */

  btnManage.addEventListener('click', function () {
    setManage(!bar.classList.contains('is-open'));
  });

  btnPrimary.addEventListener('click', function () {
    if (bar.classList.contains('is-open')) {
      save(swAnalytics.checked, swMarketing.checked, root.dataset.toastAll);
    } else {
      save(true, true, root.dataset.toastAccept);
    }
  });

  btnSecondary.addEventListener('click', function () {
    if (bar.classList.contains('is-open')) {
      save(true, true, root.dataset.toastAccept);
    } else {
      save(false, false, root.dataset.toastNecessary);
    }
  });

  /* Footer "Cookie preferences" — the way back in once a choice is stored. */
  document.addEventListener('click', function (ev) {
    var trigger = ev.target && ev.target.closest && ev.target.closest('[data-ck-open]');
    if (!trigger) return;
    ev.preventDefault();
    openBar(true);
  });

  /* A decision is being asked for, so focus stays inside the bar — Escape is
     the way out, and on a first visit it counts as "necessary only" (the
     privacy-safe reading of walking away). */
  document.addEventListener('keydown', function (ev) {
    if (!visible) return;
    if (ev.key === 'Escape') {
      // in the theme editor Escape just closes the preview — it must not write
      // a consent record on the merchant's behalf
      if (reopened || designMode) dismissReopened();
      else save(false, false, root.dataset.toastNecessary);
      return;
    }
    if (ev.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (!bar.contains(document.activeElement)) {
      ev.preventDefault();
      (ev.shiftKey ? last : first).focus();
    } else if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('resize', measure);
  window.addEventListener('scroll', measure, { passive: true });

  /* ----------------------------------------------------------------- boot */

  function boot() {
    setManage(false);
    measure();

    // the footer link is markup-only until the banner is actually on the page
    Array.prototype.forEach.call(document.querySelectorAll('[data-ck-footer]'), function (el) {
      el.hidden = false;
    });

    if (designMode) {
      // the theme editor drives visibility through section select/deselect
      released = true;
      return;
    }

    var stored = readStored();
    if (stored) {
      applyConsent(stored);
      released = true; // a returning visitor: the bubble runs its normal flow
      return;
    }

    loadPrivacy(function (api) {
      var consent = api && typeof api.getTrackingConsent === 'function' ? api.getTrackingConsent() : null;
      if (consent && consent !== 'no_interaction') {
        // a choice already exists (the previous banner, or another surface) —
        // carry it into our own memory instead of asking a second time
        writeStored({
          necessary: true,
          analytics: !!(api.analyticsProcessingAllowed && api.analyticsProcessingAllowed()),
          marketing: !!(api.marketingAllowed && api.marketingAllowed()),
          ts: Date.now()
        });
        release(0);
        return;
      }
      swAnalytics.checked = root.dataset.analyticsDefault === 'true';
      swMarketing.checked = false;
      later(showBar, delay);
    });
  }

  /* Theme editor: the banner follows the section the merchant has selected. */
  document.addEventListener('shopify:section:select', function (ev) {
    if (ev.target && ev.target.id && ev.target.id.indexOf('cookie-banner') !== -1) openBar(false);
  });
  document.addEventListener('shopify:section:deselect', function (ev) {
    if (ev.target && ev.target.id && ev.target.id.indexOf('cookie-banner') !== -1) hideBar();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
